import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [comic, setComic] = useState<Comic | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('chapters');
  const [readingChapter, setReadingChapter] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setLoading(true);
    const found = comics.find((c) => c.id === Number(id));
    setComic(found || null);
    setLoading(false);
    setReadingChapter(null);
    setActiveTab('chapters');
  }, [id]);

  const relatedComics = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug)
      .slice(0, 5);
  }, [comic]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="mt-3 text-foreground-500 text-sm">加载中...</p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-foreground-600 text-lg">漫画未找到</p>
            <Link
              to="/"
              className="mt-4 inline-block px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 虚空韩漫网`,
        url: `https://szlzjsm.cn/comic/${comic.id}`,
        author: { '@type': 'Person', name: comic.author },
        datePublished: comic.chapters[comic.chapters.length - 1]?.updateDate || comic.updateDate,
        dateModified: comic.updateDate,
        description: comic.description,
        image: comic.coverImage,
        publisher: {
          '@type': 'Organization',
          name: '虚空韩漫网',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://szlzjsm.cn' },
          {
            '@type': 'ListItem',
            position: 2,
            name: `${comic.category}韩漫`,
            item: `https://szlzjsm.cn/category/${comic.categorySlug}`,
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: comic.title,
            item: `https://szlzjsm.cn/comic/${comic.id}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: `${comic.category}韩漫`, path: `/category/${comic.categorySlug}` },
              { label: comic.title },
            ]}
          />

          {/* Comic Header */}
          <section className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-[5/7] rounded-lg overflow-hidden">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  title={`${comic.title} - 虚空韩漫网`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="text-sm text-foreground-500">
                  作者：<strong className="text-foreground-800">{comic.author}</strong>
                </span>
                <span
                  className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-foreground-100 text-foreground-600'
                  }`}
                >
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-500 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {comic.rating}
                </span>
                <span className="text-sm text-foreground-500">
                  {comic.viewCount > 10000
                    ? `${(comic.viewCount / 10000).toFixed(1)}万` : comic.viewCount} 阅读
                </span>
                <span className="text-sm text-foreground-500">
                  {comic.likeCount > 10000
                    ? `${(comic.likeCount / 10000).toFixed(1)}万` : comic.likeCount} 点赞
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-4">
                {comic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-6">
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1 w-fit">
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500'
                    }`}
                  >
                    章节列表
                  </button>
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeTab === 'info'
                        ? 'bg-background-50 text-foreground-900 shadow-sm'
                        : 'text-foreground-500'
                    }`}
                  >
                    作品简介
                  </button>
                </div>
              </div>

              {activeTab === 'info' && (
                <div className="mt-5">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    {comic.description}
                  </p>
                </div>
              )}

              {activeTab === 'chapters' && (
                <div className="mt-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Link
                      to={`/comic/${comic.id}/read/${comic.chapters[0]?.number || 1}`}
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                    >
                      开始阅读
                    </Link>
                    <button className="px-6 py-2.5 rounded-full bg-secondary-500 text-background-50 text-sm font-medium hover:bg-secondary-600 transition-all whitespace-nowrap cursor-pointer flex items-center gap-1">
                      <i className="ri-heart-line"></i> 收藏
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-80 overflow-y-auto pr-2">
                    {comic.chapters.map((chapter) => (
                      <Link
                        key={chapter.id}
                        to={`/comic/${comic.id}/read/${chapter.number}`}
                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-all cursor-pointer"
                      >
                        <span className="text-sm text-foreground-800 truncate">
                          {chapter.title}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">
                          {chapter.updateDate}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="py-14 border-t border-background-200/70">
              <div className="mb-6">
                <h2 className="text-lg md:text-2xl font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <Link
                    key={c.id}
                    to={`/comic/${c.id}`}
                    className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                  >
                    <div className="aspect-[5/7] overflow-hidden">
                      <img
                        src={c.coverImage}
                        alt={c.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-heading font-bold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {c.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{c.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </>
  );
}